import React from 'react';
import { fs, makeFields, FarAiAssist, Getter, Setter } from './farFields';

/* FAR Part 13 — Simplified Acquisition Procedures (7 sections).
 * Shown when the CO-selected method is Part 13. */

// Configurable threshold / authority options for the dropdown (edit when FAR changes).
const THRESHOLD_OPTIONS = [
  'Micro-Purchase Threshold ($10,000)',
  'Simplified Acquisition Threshold ($250,000)',
  'Commercial SAT under FAR 13.5 ($7.5M)',
  'Emergency/Contingency Micro-Purchase (increased)',
  'Emergency/Contingency SAT (increased)',
  'Other authorized threshold',
];

export default function Far13Form({ get, set }: { get: Getter; set: Setter }) {
  const F = makeFields(get, set);

  return (
    <div>
      <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>FAR Part 13 — Simplified Acquisition Procedures</span>
        <p style={{ fontSize: 11, color: '#1d4ed8', margin: '2px 0 0' }}>Streamlined procedures for acquisitions within simplified acquisition authority.</p>
      </div>
      <FarAiAssist formName="FAR Part 13 — Simplified Acquisition" get={get} set={set}
        fields={[
          { key: 'elig_rationale', label: 'Part 13 Eligibility Rationale', kind: 'long' },
          { key: 'sb_rationale', label: 'Set-Aside Rationale', kind: 'long' },
          { key: 'qe_price_analysis', label: 'Price Analysis', kind: 'long' },
          { key: 'aw_rationale', label: 'Award Rationale', kind: 'long' },
        ]} />

      {/* Section 1 — Part 13 Eligibility */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 1 · Part 13 Eligibility</h3>
        {F.Long('elig_description', 'Requirement Description')}
        {F.Money('elig_value', 'Estimated Value Including Options')}
        {F.YN('elig_within_authority', 'Within Applicable Simplified Acquisition Authority?', ['Yes', 'No', 'Legal Review Required'])}
        {F.YN('elig_commercial', 'Is This a Commercial Product or Service?')}
        {F.YN('elig_135', 'Is FAR Subpart 13.5 Authority Being Used?')}
        {F.YN('elig_split', 'Is the Requirement Being Split to Remain Below a Threshold?', ['Yes', 'No'])}
        {get('elig_split') === 'Yes' && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#991b1b', marginBottom: 12 }}>
            Requirements may not be split to stay below a threshold. This must be "No" to proceed under Part 13.
          </div>
        )}
        {F.Long('elig_rationale', 'Part 13 Eligibility Rationale')}
        {F.Sel('elig_threshold', 'Applicable Threshold or Emergency Authority', THRESHOLD_OPTIONS)}
      </div>

      {/* Section 2 — Competition and Small Business */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 2 · Competition and Small Business</h3>
        {F.YN('sb_review_completed', 'Small-Business Set-Aside Review Completed?')}
        {F.Sel('sb_decision', 'Set-Aside Decision', ['Total small-business set-aside', 'Partial set-aside', 'Socioeconomic program', 'Unrestricted', 'Sole source under authorized program', 'Not applicable'])}
        {F.Long('sb_rationale', 'Set-Aside Rationale')}
        {F.Text('sb_sources_identified', 'Number of Potential Sources Identified')}
        {F.YN('sb_max_competition', 'Maximum Practicable Competition Expected?')}
        {F.Sel('sb_solicitation_method', 'Solicitation Method', ['Oral quotation', 'Written RFQ', 'Electronic RFQ', 'Combined synopsis/solicitation', 'Other simplified method'])}
        {F.Text('sb_vendors_solicited', 'Number of Vendors Solicited')}
        {F.TableField('sb_vendor_record', 'Vendor Solicitation Record', [
          { key: 'vendor', label: 'Vendor', type: 'text' },
          { key: 'sb_status', label: 'Small-business status', type: 'text' },
          { key: 'solicited_date', label: 'Solicited date', type: 'text' },
          { key: 'response', label: 'Response', type: 'text' },
          { key: 'quote_received', label: 'Quote received', type: 'select', options: ['Yes', 'No'] },
        ])}
      </div>

      {/* Section 3 — Limited Competition or Sole Source */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 3 · Limited Competition or Sole Source</h3>
        {F.YN('lc_limited', 'Is Competition Limited?')}
        {get('lc_limited') === 'Yes' && <>
          {F.Sel('lc_authority', 'Authority or Circumstance', ['Only one responsible source', 'Unusual and compelling urgency', 'Authorized or required by statute', 'National security', 'Public interest', 'Other authorized circumstance'])}
          {F.Long('lc_reason', 'Reason Competition Is Limited')}
          {F.Long('lc_market_research', 'Market Research Supporting the Decision')}
          {F.YN('lc_brand_name', 'Brand-Name Requirement Included?')}
          {F.Text('lc_justification_doc', 'Justification Document (reference)')}
          {F.YN('lc_approval', 'Required Approval Obtained?', ['Yes', 'No', 'Pending'])}
          {F.YN('lc_posting', 'Posting Requirement Completed?', ['Yes', 'No', 'Not Applicable'])}
        </>}
      </div>

      {/* Section 4 — RFQ and Quote Requirements */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 4 · RFQ and Quote Requirements</h3>
        {F.Text('rfq_number', 'RFQ Number')}
        {F.Long('rfq_description', 'Description of Supplies or Services')}
        {F.TableField('rfq_line_items', 'Quantity and Unit', [
          { key: 'item', label: 'Item', type: 'text' },
          { key: 'description', label: 'Description', type: 'text' },
          { key: 'quantity', label: 'Quantity', type: 'text' },
          { key: 'unit', label: 'Unit', type: 'text' },
        ])}
        {F.Long('rfq_delivery', 'Delivery or Performance Requirements')}
        {F.Text('rfq_deadline', 'Submission Deadline (date & time)')}
        {F.Sel('rfq_submission_method', 'Quote Submission Method', ['Email', 'Electronic portal', 'Oral', 'Mail/hand delivery', 'Other'])}
        {F.Long('rfq_evaluation_basis', 'Evaluation Basis')}
        <div style={fs.field}>
          <label style={fs.label}>Required Quote Content</label>
          {F.Checklist('rfq_required_content', ['Price', 'Technical information', 'Delivery schedule', 'Past performance', 'Representations and certifications', 'Terms and conditions', 'Other'])}
        </div>
        {F.ListField('rfq_provisions_clauses', 'Applicable Provisions and Clauses')}
      </div>

      {/* Section 5 — Quote Receipt and Evaluation */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 5 · Quote Receipt and Evaluation</h3>
        {F.TableField('qe_vendors', 'Vendor Evaluation', [
          { key: 'vendor', label: 'Vendor', type: 'text' },
          { key: 'on_time', label: 'On time?', type: 'select', options: ['Yes', 'No'] },
          { key: 'price', label: 'Total evaluated price', type: 'text' },
          { key: 'technical', label: 'Technical acceptability', type: 'select', options: ['Acceptable', 'Unacceptable'] },
          { key: 'delivery', label: 'Delivery compliance', type: 'select', options: ['Yes', 'No'] },
          { key: 'past_perf', label: 'Past performance', type: 'text' },
          { key: 'responsibility', label: 'Responsibility', type: 'select', options: ['Confirmed', 'Pending', 'Failed'] },
          { key: 'strengths', label: 'Strengths', type: 'long' },
          { key: 'weaknesses', label: 'Weaknesses', type: 'long' },
          { key: 'clarification', label: 'Clarification required?', type: 'select', options: ['Yes', 'No'] },
          { key: 'result', label: 'Overall result', type: 'select', options: ['Eligible', 'Ineligible', 'Selected'] },
        ])}
        {F.Sel('qe_method', 'Evaluation Method', ['Price', 'LPTA', 'Best Value', 'Comparative Evaluation'])}
        {F.YN('qe_price_reasonable', 'Price Reasonableness Determined?')}
        {F.Long('qe_price_analysis', 'Price Analysis')}
        {F.YN('qe_clarifications', 'Clarifications Conducted?')}
        {F.Text('qe_evaluation_record', 'Evaluation Record (reference)')}
      </div>

      {/* Section 6 — Purchase Method and Award */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 6 · Purchase Method and Award</h3>
        {F.Sel('aw_purchase_method', 'Purchase Method', ['Purchase order', 'Blanket purchase agreement', 'Governmentwide commercial purchase card', 'Imprest fund', 'Third-party draft', 'Other authorized method'])}
        {F.Text('aw_selected_vendor', 'Selected Vendor')}
        {F.Money('aw_amount', 'Award Amount')}
        {F.Long('aw_rationale', 'Award Rationale')}
        {F.YN('aw_responsibility', 'Vendor Responsibility Verified?')}
        {F.YN('aw_sam', 'SAM Registration Verified?', ['Yes', 'No', 'Not Applicable'])}
        {F.YN('aw_funding', 'Funding Verified?')}
        {F.YN('aw_approvals', 'Required Approvals Complete?')}
        {F.Text('aw_award_doc', 'Award Document (reference)')}
        {F.YN('aw_notified', 'Unsuccessful Vendors Notified?', ['Yes', 'No', 'Not Applicable'])}
      </div>

      {/* Section 7 — Final Review */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 7 · FAR Part 13 Final Review</h3>
        {F.Checklist('final_review', [
          'Simplified acquisition authority is applicable.',
          'Requirement was not improperly split.',
          'Small-business set-aside review was completed.',
          'Maximum practicable competition was sought.',
          'Limited competition is documented and approved.',
          'Vendors received consistent information.',
          'Quotes were evaluated using the stated basis.',
          'Price reasonableness was documented.',
          'Vendor responsibility was verified.',
          'Funding is available.',
          'Award rationale is documented.',
          'Required notifications were completed.',
          'Acquisition record is complete.',
        ])}
      </div>
    </div>
  );
}
